/* ── Klick/tryck i 3D-vyn ─────────────────────────────
 * Pekarbaserad (pointer events) igenkänning av klick, tryck och dubbeltryck, så att
 * urval fungerar likadant med mus, finger och penna. Webbläsarens eget "click" kommer
 * även efter ett kameradrag och, på pekskärm, först efter efterhärmade mushändelser —
 * därför räknas här bara korta tryck med liten rörelse som klick.
 */

const TAP_MAX_MS = 400; // längre tryck räknas som drag/håll, inte klick
const TAP_MAX_MOVE = 8; // px — mer rörelse än så är ett kameradrag
const DOUBLE_TAP_MS = 350; // två tryck inom denna tid räknas som dubbeltryck
const DOUBLE_TAP_DIST = 24; // px — fingrar träffar sällan exakt samma punkt två gånger

const INTERACTIVE = 'button, a, input, textarea, [role="button"]';

/**
 * Kopplar klick-, tryck-, dubbeltryck- och hoverlyssnare till visarens container.
 * Motoroberoende: handlarna får pekarhändelsen och läser själva clientX/clientY.
 * @param {HTMLElement} container
 * @param {{
 *   onTap: (event: PointerEvent) => void,
 *   onDoubleTap: (event: PointerEvent) => void,
 *   onHover: (event: PointerEvent) => void,
 *   onLeave?: () => void,
 * }} handlers
 * @returns {{ detach: () => void, lastPointerType: () => string }}
 */
export function attachCanvasPointer(container, { onTap, onDoubleTap, onHover, onLeave }) {
  /** pointerId -> { x, y, t, maxMove } */
  const active = new Map();
  let multi = false; // nyp/tvåfingersgest pågår — inget klick
  let lastTap = null;
  let lastType = 'mouse';

  const reset = (id) => {
    active.delete(id);
    if (active.size === 0) multi = false;
  };

  const onDown = (e) => {
    lastType = e.pointerType || 'mouse';
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target?.closest?.(INTERACTIVE)) return;
    if (active.size > 0) { multi = true; lastTap = null; }
    active.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now(), maxMove: 0 });
  };

  const onMove = (e) => {
    const p = active.get(e.pointerId);
    if (p) {
      const d = Math.hypot(e.clientX - p.x, e.clientY - p.y);
      if (d > p.maxMove) p.maxMove = d;
    }
    // Hover bara med mus och inte under ett kameradrag (ingen raycast per bildruta vid rotation).
    if (e.pointerType === 'mouse' && active.size === 0) onHover(e);
  };

  const onUp = (e) => {
    const p = active.get(e.pointerId);
    if (!p) return;
    const wasMulti = multi;
    reset(e.pointerId);
    if (wasMulti) { lastTap = null; return; }
    const now = performance.now();
    const moved = Math.max(p.maxMove, Math.hypot(e.clientX - p.x, e.clientY - p.y));
    if (now - p.t > TAP_MAX_MS || moved >= TAP_MAX_MOVE) { lastTap = null; return; }

    onTap(e);

    // Mus har webbläsarens dblclick; finger och penna får egen dubbeltrycksigenkänning.
    if (e.pointerType === 'mouse') return;
    if (lastTap && now - lastTap.t <= DOUBLE_TAP_MS
      && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) <= DOUBLE_TAP_DIST) {
      lastTap = null;
      onDoubleTap(e);
    } else {
      lastTap = { x: e.clientX, y: e.clientY, t: now };
    }
  };

  const onCancel = (e) => { reset(e.pointerId); lastTap = null; };
  // Släpps musknappen utanför visaren når pointerup aldrig containern — städa upp ändå.
  const onWindowUp = (e) => { if (active.has(e.pointerId)) reset(e.pointerId); };
  const onPointerLeave = (e) => { if (e.pointerType === 'mouse') onLeave?.(); };

  container.addEventListener('pointerdown', onDown, { passive: true });
  container.addEventListener('pointermove', onMove, { passive: true });
  container.addEventListener('pointerup', onUp, { passive: true });
  container.addEventListener('pointercancel', onCancel, { passive: true });
  container.addEventListener('pointerleave', onPointerLeave, { passive: true });
  window.addEventListener('pointerup', onWindowUp, { passive: true });
  window.addEventListener('pointercancel', onWindowUp, { passive: true });

  return {
    detach() {
      container.removeEventListener('pointerdown', onDown);
      container.removeEventListener('pointermove', onMove);
      container.removeEventListener('pointerup', onUp);
      container.removeEventListener('pointercancel', onCancel);
      container.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('pointerup', onWindowUp);
      window.removeEventListener('pointercancel', onWindowUp);
    },
    lastPointerType: () => lastType,
  };
}
